import { computed, ref, type Ref } from 'vue';
import { Accessory, Adapter } from '@/models/installationPoint/Accessory';
import type { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import type { InspectionPoint } from '@/models/inspectionPoint/InspectionPoint';
import { AccessoryService } from '@/models/installationPoint/AccessoryService';
import { useOrderStore } from '@/store/order/orderStore';
import {
  AccessoryType,
  AdapterServiceReasonEnum,
  AdapterServiceTypeEnum,
  adapterTypeList,
} from '@/enums/device';
import {
  EntityTransactionType,
  TransactionDiffType,
} from '@/models/OrderChanges';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';

export interface DisplayAdapterItem {
  adapter: Adapter;
  service?: AccessoryService;
  isReplacement?: boolean;
  replacementService?: AccessoryService;
}

export interface HandleExchangePayload {
  oldAdapter: Adapter;
  newAdapter: Adapter;
  service?: AccessoryService;
}

export interface ServiceTypeChange {
  oldAdapter: Adapter;
  serviceTypeValue: string;
}

export function useAdapters(
  installationPointRef: Ref<InstallationPoint | InspectionPoint>,
  accessoryServiceListRef: Ref<Record<string, AccessoryService> | undefined>
) {
  const createdAdapters = ref<Adapter[]>([]);

  const accessories = computed(() => {
    return installationPointRef.value.accessories.filter(
      (accessory: Accessory) =>
        accessory.type !== AccessoryType.SRT_BATTERY_PACK &&
        !accessory.unmountedAt
    ) as Adapter[];
  });

  const displayAdapters = computed<DisplayAdapterItem[]>(() => {
    const services = accessoryServiceListRef.value ?? {};
    return accessories.value.map((adapter) => {
      const service = services[adapter.id];
      const replacementService = Object.values(services).find(
        (s: any) => s && s.newAccessoryId === adapter.id
      );
      const isReplacement = !!replacementService;
      return { adapter, service, isReplacement, replacementService };
    });
  });

  const isNewlyCreated = (adapter: Adapter) => {
    return createdAdapters.value.some((a) => a.id === adapter.id);
  };

  const isNewAccessory = (adapter: Adapter): boolean => {
    if (!adapter.mountedAt) return true;
    if (isNewlyCreated(adapter)) return true;

    const orderChanges = useOrderStore().getOrderChanges();
    const entityTransactions =
      orderChanges?.transactions[installationPointRef.value.id];
    if (!entityTransactions) return false;

    if (entityTransactions.type === EntityTransactionType.CREATE_ENTITY) {
      return true;
    }

    if (entityTransactions.type === EntityTransactionType.UPDATE_ENTITY) {
      return entityTransactions.diff.some(
        (diff: any) =>
          diff.type === TransactionDiffType.CREATE &&
          diff.path.includes(adapter.id)
      );
    }

    return false;
  };

  const ensureServiceList = () => {
    if (!accessoryServiceListRef.value)
      accessoryServiceListRef.value = {} as any;
  };

  const addAdapter = (baseAdapter?: Adapter) => {
    const adapter =
      baseAdapter ?? new Adapter({ type: AccessoryType.SRT_ADAPTER });
    if (!adapter.mountedAt) {
      (adapter as any).mountedAt = new Date();
    }

    installationPointRef.value.accessories.push(adapter);
    createdAdapters.value.push(adapter);

    ensureServiceList();
    accessoryServiceListRef.value![adapter.id] = new AccessoryService()
      .setAccessoryId(adapter.id)
      .setType(AdapterServiceTypeEnum.MOUNT);

    return adapter;
  };

  const removeAdapter = (adapter: Adapter) => {
    onAdapterChange(adapter);

    // Never allow removing a replacement adapter created by an exchange
    const isReplacement = !!Object.values(
      accessoryServiceListRef.value ?? {}
    ).find((s) => (s as any)?.newAccessoryId === adapter.id);
    if (isReplacement) {
      return;
    }

    // Only allow removal of adapters created in this session. Originals cannot be removed here.
    if (!isNewAccessory(adapter)) {
      return;
    }

    // Remove from model and created list
    installationPointRef.value.accessories =
      installationPointRef.value.accessories.filter((a) => a.id !== adapter.id);
    createdAdapters.value = createdAdapters.value.filter(
      (a) => a.id !== adapter.id
    );
    // Remove potential service
    if (accessoryServiceListRef.value?.[adapter.id]) {
      delete accessoryServiceListRef.value[adapter.id];
    }
  };

  const handleExchange = ({
    oldAdapter,
    newAdapter,
    service,
  }: HandleExchangePayload) => {
    ensureServiceList();

    const originalService = accessoryServiceListRef.value?.[oldAdapter.id];
    const previousNewId = originalService?.newAccessoryId;

    if (previousNewId) {
      const prevIdx = installationPointRef.value.accessories.findIndex(
        (a) => a.id === previousNewId
      );
      if (prevIdx !== -1) {
        installationPointRef.value.accessories.splice(prevIdx, 1);
      }

      const createdIdx = createdAdapters.value.findIndex(
        (a) => a.id === previousNewId
      );
      if (createdIdx !== -1) {
        createdAdapters.value.splice(createdIdx, 1);
      }

      if (
        accessoryServiceListRef.value &&
        accessoryServiceListRef.value[previousNewId]
      ) {
        delete accessoryServiceListRef.value[previousNewId];
      }
    }

    const idx = installationPointRef.value.accessories.findIndex(
      (a) => a.id === oldAdapter.id
    );
    if (idx !== -1) {
      installationPointRef.value.accessories.splice(idx + 1, 0, newAdapter);
    } else {
      installationPointRef.value.accessories.push(newAdapter);
    }

    if (service) {
      if (!service.accessoryId) service.accessoryId = oldAdapter.id;
      accessoryServiceListRef.value![oldAdapter.id] = service;
    }

    const s = accessoryServiceListRef.value?.[oldAdapter.id];
    if (s) {
      (s as any).newAccessoryId = newAdapter.id;
      (s as any).isExchangedAdapter = true;
    }
  };

  const onAdapterChange = (adapter: Adapter) => {
    const adapterPair = adapterTypeList.find(
      (newAdapter) => newAdapter.customizedNumber === adapter.customizedNumber
    );
    if (adapterPair) {
      adapter.productKey = adapterPair.productKey;
      useInstallationPointStore().addToRecentAdapterList(adapter);
    } else {
      adapter.productKey = '';
    }
  };

  const handleServiceTypeChange = ({ oldAdapter }: ServiceTypeChange) => {
    const existingAdapterService =
      accessoryServiceListRef.value?.[oldAdapter.id];

    const existingAdapterId = existingAdapterService?.newAccessoryId;
    const idx = installationPointRef.value.accessories.findIndex(
      (a) => a.id === existingAdapterId
    );
    if (idx !== -1) {
      installationPointRef.value.accessories.splice(idx, 1);
    }
  };

  const isFormValid = computed(() => {
    const hasInvalidAccessory = installationPointRef.value?.accessories.find(
      (accessory) => accessory instanceof Adapter && !accessory.productKey
    );

    if (hasInvalidAccessory) return false;

    const hasInvalidService = Object.values(
      accessoryServiceListRef.value || {}
    ).find((service: AccessoryService) => {
      return (
        (service.type === AdapterServiceTypeEnum.EXCHANGE && !service.reason) ||
        (service.type &&
          service.reason === AdapterServiceReasonEnum.OTHER &&
          !service.note)
      );
    });
    return !hasInvalidService;
  });

  return {
    displayAdapters,
    addAdapter,
    removeAdapter,
    handleExchange,
    onAdapterChange,
    handleServiceTypeChange,
    createdAdapters,
    isFormValid,
  };
}
